/* eslint-disable jsx-a11y/alt-text */
import { TxtInput } from "../../../components";
import * as React from "react";
import { LeadModel } from "../../../redux/types";
import moment from "moment";
import { Box, Drawer, useMediaQuery } from "@mui/material";
import { logEvent, LEAD_EVENTS } from "../../../utils/amplitude";
import { LEAD_DRAWER_DETAILS } from "../../../contants/projectDetails";
import { LeadDrawerDetails } from "../../../types/type";
import DisplayLeadDetailsCard from "./DisplayLeadDetailCard";

import searchIcon from "../../../images/searchIcon.png";
import cancelIcon from "../../../images/CancelIcon.png";

import {
  PageContainer,
  ContentWrapper,
  HeaderSection,
  TitleRow,
  PageTitle,
  PageSubtitle,
  SearchInputBox,
  SearchIcon,
  TableWrapper,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  PrimaryCell,
  StatusBadge,
  SecondaryText,
  PaginationWrapper,
  PaginationText,
  EmptyState,
  EmptyStateText,
} from "../../../components/table/CommonTable.styled";

interface UserTableProps {
  leads: LeadModel[];
}

export const LeadsTable: React.FC<UserTableProps> = ({ leads }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(10);
  const [keyword, setKeyword] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [selectedLead, setSelectedLead] = React.useState<LeadModel | null>(null);

  const handleSearchIconClick = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const filterLeads = () => {
    if (keyword.length > 0 && Array.isArray(leads)) {
      return leads.filter((item) => {
        return (
          item.streetAddress.toLowerCase().includes(keyword.toLowerCase()) &&
          item.utm !== "waitlist"
        );
      });
    }

    return leads.filter((item) => item.utm !== "waitlist");
  };

  const goToPreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const goToNextPage = () => {
    setPage((prevPage) =>
      Math.min(prevPage + 1, Math.ceil(filterLeads().length / rowsPerPage) - 1)
    );
  };

  const handleDrawerOpen = (lead: LeadModel) => {
    return () => {
      setSelectedLead(lead);
      setOpenDrawer(true);
      
      logEvent(LEAD_EVENTS.LEAD_VIEWED, { 
        lead_id: lead.id, 
        lead_address: lead.streetAddress, 
        utm_source: lead.utm 
      });
    };
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };

  const getSourceLabel = (lead: LeadModel) => {
    if (lead.utm === "referral" || lead.utm === "sales") {
      return `${lead.utm.charAt(0).toUpperCase() + lead.utm.slice(1)} • ${lead.refId}`;
    }
    return lead.utm.charAt(0).toUpperCase() + lead.utm.slice(1);
  };

  const showLeadsDetails = () => {
    return (
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleDrawerClose}
        sx={{
          "& .MuiDrawer-paper": {
            height: "100%",
            width: isMobile ? "100%" : "420px",
            borderTopLeftRadius: isMobile ? 0 : "16px",
            borderBottomLeftRadius: isMobile ? 0 : "16px",
            overflowX: "hidden",
          },
        }}
      >
        <Box sx={{ p: 3, height: "100%", backgroundColor: "#fff" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Box
              onClick={handleDrawerClose}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                backgroundColor: "#f3f4f6",
                transition: "background-color 0.2s",
                "&:hover": { backgroundColor: "#e5e7eb" },
              }}
            >
              <img src={cancelIcon} alt="Close" style={{ width: 12, height: 12 }} />
            </Box>
            <Box>
              <Box sx={{ fontSize: 18, fontWeight: 600, color: "#1a1a2e" }}>
                Lead #{selectedLead?.id}
              </Box>
              <Box sx={{ fontSize: 13, color: "#6b7280" }}>
                {selectedLead?.streetAddress}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {LEAD_DRAWER_DETAILS.map((detail: LeadDrawerDetails, index: number) => (
              <DisplayLeadDetailsCard
                key={`lead-detail-${index}`}
                label={detail.label}
                value={
                  detail.isDate
                    ? selectedLead?.[detail.key as keyof LeadModel]
                      ? new Date(selectedLead?.[detail.key as keyof LeadModel] as string).toLocaleDateString()
                      : ""
                    : (selectedLead?.[detail.key as keyof LeadModel] as string) || ""
                }
              />
            ))}
          </Box>
        </Box>
      </Drawer>
    );
  };

  const renderLeadsTable = () => {
    const filteredLeads = filterLeads();
    const paginatedLeads = [...filteredLeads]
      .reverse()
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <TableWrapper>
        <TableHeader>
          <TableHeaderCell width="45%">Address</TableHeaderCell>
          <TableHeaderCell width="25%">Source</TableHeaderCell>
          <TableHeaderCell width="20%">Created</TableHeaderCell>
          <TableHeaderCell width="10%"></TableHeaderCell>
        </TableHeader>

        {filteredLeads.length === 0 ? (
          <EmptyState>
            <EmptyStateText>No leads found</EmptyStateText>
          </EmptyState>
        ) : (
          paginatedLeads.map((lead, index) => (
            <TableRow key={`lead-${lead.id}-${index}`} onClick={handleDrawerOpen(lead)}>
              <PrimaryCell width="45%" mobileLabel="Address">
                {lead.streetAddress}, {lead.city}
              </PrimaryCell>
              <TableCell width="25%" mobileLabel="Source">
                <StatusBadge variant={lead.utm === "referral" ? "success" : lead.utm === "sales" ? "info" : "warning"}>
                  {getSourceLabel(lead)}
                </StatusBadge>
              </TableCell>
              <TableCell width="20%" mobileLabel="Created">
                <SecondaryText>{moment(lead.createdAt).fromNow(true)} ago</SecondaryText>
              </TableCell>
              <TableCell width="10%" style={{ textAlign: "right" }}>
                <Box
                  component="span"
                  sx={{
                    color: "#4b87ed",
                    fontSize: 13,
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  View →
                </Box>
              </TableCell>
            </TableRow>
          ))
        )}

        {filteredLeads.length > 0 && (
          <PaginationWrapper>
            <Box
              component="button"
              onClick={goToPreviousPage}
              disabled={page === 0}
              sx={{
                px: 2,
                py: 1,
                border: "none",
                borderRadius: "6px",
                backgroundColor: page === 0 ? "#f3f4f6" : "#e5e7eb",
                color: page === 0 ? "#9ca3af" : "#374151",
                fontSize: 13,
                fontWeight: 500,
                cursor: page === 0 ? "not-allowed" : "pointer",
                "&:hover": page === 0 ? {} : { backgroundColor: "#d1d5db" },
              }}
            >
              ← Prev
            </Box>
            <PaginationText>
              Page {page + 1} of {Math.ceil(filteredLeads.length / rowsPerPage)}
            </PaginationText>
            <Box
              component="button"
              onClick={goToNextPage}
              disabled={page >= Math.ceil(filteredLeads.length / rowsPerPage) - 1}
              sx={{
                px: 2,
                py: 1,
                border: "none",
                borderRadius: "6px",
                backgroundColor: page >= Math.ceil(filteredLeads.length / rowsPerPage) - 1 ? "#f3f4f6" : "#e5e7eb",
                color: page >= Math.ceil(filteredLeads.length / rowsPerPage) - 1 ? "#9ca3af" : "#374151",
                fontSize: 13,
                fontWeight: 500,
                cursor: page >= Math.ceil(filteredLeads.length / rowsPerPage) - 1 ? "not-allowed" : "pointer",
                "&:hover": page >= Math.ceil(filteredLeads.length / rowsPerPage) - 1 ? {} : { backgroundColor: "#d1d5db" },
              }}
            >
              Next →
            </Box>
          </PaginationWrapper>
        )}
      </TableWrapper>
    );
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <HeaderSection>
          <TitleRow>
            <PageTitle>Leads</PageTitle>
          </TitleRow>
          <PageSubtitle>{filterLeads().length} total leads</PageSubtitle>
        </HeaderSection>

        <Box sx={{ mb: 3 }}>
          <SearchInputBox>
            <SearchIcon
              src={searchIcon}
              style={{ margin: "0 12px" }}
              onClick={() => inputRef.current?.focus()}
            />
            <TxtInput
              placeholder="Search by address..."
              onChange={setKeyword}
              inputRef={inputRef}
              background="white"
              width="100%"
            />
          </SearchInputBox>
        </Box>

        {Array.isArray(leads) && renderLeadsTable()}
        {showLeadsDetails()}
      </ContentWrapper>
    </PageContainer>
  );
};
